import classNames from 'classnames';
import type { AlertProps, AlertType } from './AlertV2.types';

type AlertStyles = {
  wrapper: string;
  icon: string;
  title: string;
  subtitle: string;
  primaryAction: string;
  divider: string;
  secondaryAction: string;
  closeButton: string;
};

// TODO: some colors are currently hardcoded 'cos they're not in the themeColors.json yet
const AlertV2FilledColorMap: Record<AlertType, AlertStyles> = {
  error: {
    wrapper:
      'relative bg-[#FEF2F2] shadow-[0px_1px_2px_0px_rgba(153,27,27,0.12),0px_0px_0px_1px_#FEE2E2]',
    icon: 'text-danger-dark',
    title: 'text-danger-dark',
    subtitle: 'text-danger-dark',
    primaryAction: 'text-[#DC2626]',
    divider: 'text-[#F87171]',
    secondaryAction: 'text-[#F87171]',
    closeButton: 'text-[#F87171]',
  },
  warning: {
    wrapper:
      'bg-[#FFFBEB] shadow-[0px_1px_2px_0px_rgba(146,64,14,0.12),0px_0px_0px_1px_#FEF3C7]',
    icon: 'text-[#78350F]',
    title: 'text-[#78350F]',
    subtitle: 'text-[#78350F]',
    primaryAction: 'text-[#D97706]',
    divider: 'text-[#FBBF24]',
    secondaryAction: 'text-[#FBBF24]',
    closeButton: 'text-[#FBBF24]',
  },
  success: {
    wrapper:
      'bg-[#ECFDF5] shadow-[0px_1px_2px_0px_rgba(6,95,70,0.12),0px_0px_0px_1px_#D1FAE5]',
    icon: 'text-success-dark',
    title: 'text-success-dark',
    subtitle: 'text-success-dark',
    primaryAction: 'text-[#059669]',
    divider: 'text-[#34D399]',
    secondaryAction: 'text-[#34D399]',
    closeButton: 'text-green-400',
  },
  information: {
    wrapper:
      'bg-[#EFF6FF] shadow-[0px_1px_2px_0px_rgba(30,64,175,0.12),0px_0px_0px_1px_#DBEAFE]',
    icon: 'text-[#1E3A8A]',
    title: 'text-[#1E3A8A]',
    subtitle: 'text-[#1E3A8A]',
    primaryAction: 'text-[#2563EB]',
    divider: 'text-[#60A5FA]',
    secondaryAction: 'text-[#60A5FA]',
    closeButton: 'text-[#60A5FA]',
  },
  neutre: {
    wrapper:
      'bg-primary-25 shadow-[0px_1px_2px_0px_rgba(27,27,27,0.10),0px_0px_0px_1px_#E7E7E7]',
    icon: 'text-primary-950',
    title: 'text-primary-950',
    subtitle: 'text-primary-600',
    primaryAction: 'text-primary-950',
    divider: 'text-primary-600',
    secondaryAction: 'text-primary-600',
    closeButton: 'text-primary-400',
  },
  primary: {
    wrapper:
      'bg-taxes-50 shadow-[0px_1px_2px_0px_rgba(93,72,112,0.12),0px_0px_0px_1px_#DDD6F9]',
    icon: 'text-taxes-950',
    title: 'text-taxes-950',
    subtitle: 'text-taxes-950',
    primaryAction: 'text-taxes-500',
    divider: 'text-taxes-300',
    secondaryAction: 'text-taxes-300',
    closeButton: 'text-taxes-300',
  },
  ai: {
    wrapper:
      'bg-taxes-50 shadow-[0px_1px_2px_0px_rgba(93,72,112,0.12),0px_0px_0px_1px_#DDD6F9]',
    icon: 'text-taxes-500',
    title: 'text-taxes-950',
    subtitle: 'text-taxes-950',
    primaryAction: 'text-taxes-500',
    divider: 'text-taxes-300',
    secondaryAction: 'text-taxes-300',
    closeButton: 'text-taxes-300',
  },
};

export const AlertV2PaddingMap: Record<AlertProps['size'], string> = {
  'x-small': 'p-2',
  small: 'py-2 px-3',
  'large-mid': 'p-[10px] pb-3',
  large: 'px-4 pt-[14px] pb-4',
};

export const AlertV2TextSizeMap: Record<AlertProps['size'], string> = {
  'x-small': 'text-xs',
  small: 'text-sm',
  'large-mid': 'text-sm',
  large: 'text-sm',
};

export const useAlertStyles = (
  props: Pick<AlertProps, 'type' | 'size' | 'variant' | 'className'>,
): AlertStyles => {
  const colors = AlertV2FilledColorMap[props.type];
  const padding = AlertV2PaddingMap[props.size];
  const textSize = AlertV2TextSizeMap[props.size];
  const isFilled = props.variant === 'filled';

  return {
    // wrapper styles
    wrapper: classNames(
      'rounded-lg backdrop-blur-sm inline-flex gap-2 items-start',
      padding,
      {
        [colors.wrapper]: isFilled,
        'bg-white shadow-[0px_1px_2px_0px_rgba(27,27,27,0.10),0px_0px_0px_1px_#E7E7E7]':
          !isFilled,
      },
      props.className,
    ),
    // content styles
    icon: classNames('w-6 h-6 flex-shrink-0', colors.icon),
    title: classNames('font-semibold', textSize, {
      [colors.title]: isFilled,
      'text-gray-900': !isFilled,
    }),
    subtitle: classNames(textSize, {
      [colors.subtitle]: isFilled,
      'text-gray-600': !isFilled,
    }),
    // close button styles
    closeButton: classNames(
      'hover:opacity-50 transition-opacity duration-200',
      {
        [colors.closeButton]: isFilled,
        'text-primary-400': !isFilled,
      },
    ),
    // actions styles
    primaryAction: classNames('underline font-semibold', textSize, {
      [colors.primaryAction]: isFilled,
      'text-primary': !isFilled,
    }),
    divider: classNames(textSize, {
      [colors.divider]: isFilled,
      'text-primary-600': !isFilled,
    }),
    secondaryAction: classNames('underline font-semibold', textSize, {
      [colors.secondaryAction]: isFilled,
      'text-primary-600': !isFilled,
    }),
  };
};
